import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { StyleSheet, css } from 'aphrodite';
import { ChevronDown, ChevronUp } from 'react-feather';
import { useNavigate } from 'react-router-dom';

const formatKey = (key) => {
  return key
    .split(/(?=[A-Z])/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const ProfileSummary = ({ onComplete, onBack }) => {
  const [formData, setFormData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchSummaryDetails = async () => {
      try {
        const response = await axios.get('http://127.0.0.1:8000/hospital/profile-summary');
        console.log('API Response:', response);
        setFormData(response.data);
        setLoading(false); // Set loading to false after fetching data
      } catch (error) {
        setError(error.message);
        setLoading(false); // Set loading to false even if there's an error
      }
    };
    fetchSummaryDetails();
  }, []);

  const handleConfirm = () => {
    // Navigate to the hospital's dashboard on success
    setSuccess('Profile confirmed successfully!'); // Set success message
    navigate('/app/hospital_dashboard');
  };

  // Handle loading and error states
  if (loading) return <div>Loading...</div>;
  if (error) return <div>{error}</div>;
  if (!formData) return <div>No data available.</div>; // Handle case where formData is null

  return (
    <motion.div
      className={css(styles.container)}
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <h2 className={css(styles.title)}>Summary</h2>
      {success && <div className={css(styles.success)}>{success}</div>}
      <div className={css(styles.summaryContent)}>
        <SummarySection
          title='Personal Information'
          data={formData.personalInfo}
        />
        <SummarySection
          title='Contact Information'
          data={formData.contactInfo}
        />
        <SummarySection
          title='Employees'
          data={formData.employees}
        />
        <SummarySection
          title='Facilities and Equipment'
          data={formData.facilities}
        />
        <SummarySection
          title='Social Accounts'
          data={formData.socialAccounts}
        />
      </div>
      <div className={css(styles.buttonContainer)}>
        <motion.button
          className={css(styles.button, styles.submitButton)}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleConfirm}
        >
          Confirm & Save
        </motion.button>
        <motion.button
          className={css(styles.button, styles.backButton)}
          onClick={onBack}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Edit
        </motion.button>
      </div>
    </motion.div>
  );
};

const SummarySection = ({ title, data }) => {
  const [isOpen, setIsOpen] = useState(true);
  const safeData = data || {};

  return (
    <div className={css(styles.section)}>
      <button
        className={css(styles.sectionTitle)}
        onClick={() => setIsOpen(!isOpen)}
      >
        {title}
        {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className={css(styles.sectionContent)}
          >
            {Object.entries(safeData).map(([key, value]) => (
              <div key={key} className={css(styles.dataItem)}>
                <span className={css(styles.dataLabel)}>{formatKey(key)}:</span>
                <span className={css(styles.dataValue)}>{value}</span>
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const styles = StyleSheet.create({
  container: {
    marginBottom: '2rem'
  },
  title: {
    fontSize: '1.5rem',
    marginBottom: '1rem',
    color: '#2783F7'
  },
  summaryContent: {
    backgroundColor: '#f8fafc',
    borderRadius: '8px',
    padding: '1rem'
  },
  section: {
    marginBottom: '1rem'
  },
  sectionTitle: {
    fontSize: '1.2rem',
    color: '#4a5568',
    backgroundColor: 'transparent',
    border: 'none',
    borderBottom: '1px solid #e2e8f0',
    paddingBottom: '0.25rem',
    marginBottom: '0.5rem',
    width: '100%',
    textAlign: 'left',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    cursor: 'pointer'
  },
  sectionContent: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '0.5rem 1rem',
    overflow: 'hidden'
  },
  dataItem: {
    display: 'flex',
    flexDirection: 'column',
    margin: '0.25rem 0'
  },
  dataLabel: {
    fontSize: '0.8rem',
    color: '#718096',
    marginBottom: '0.125rem'
  },
  dataValue: {
    fontSize: '0.9rem',
    color: '#2d3748'
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    marginTop: '1rem'
  },
  button: {
    border: 'none',
    padding: '0.5rem 1rem',
    fontSize: '1rem',
    borderRadius: '5px',
    cursor: 'pointer'
  },
  submitButton: {
    backgroundColor: '#2783F7',
    color: 'white'
  },
  backButton: {
    backgroundColor: '#e2e8f0',
    color: '#4a5568'
  },
  success: {
    color: 'green',
    margin: '1rem 0'
  }
});

export default ProfileSummary;
